import type { Quadrant } from './types.ts';

// Quadrant display metadata. Chip classes use the default Tailwind palette so the
// meaning is carried by label + colour together (never colour alone).
export const QUADRANT_META: Record<Quadrant, { label: string; chipClass: string }> = {
  contested_gaps: { label: 'Contested Gaps', chipClass: 'bg-amber-50 text-amber-800 ring-amber-200' },
  perception_gaps: { label: 'Perception Gaps', chipClass: 'bg-violet-50 text-violet-800 ring-violet-200' },
  foundation_priorities: { label: 'Foundation Priorities', chipClass: 'bg-rose-50 text-rose-800 ring-rose-200' },
  consistent_strengths: { label: 'Consistent Strengths', chipClass: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
};

export function quadrantLabel(q: Quadrant | null): string {
  return q ? QUADRANT_META[q].label : '—';
}

// Numeric formatting: a stored null renders as an em dash, never "0" or "NaN".
export function fmt(n: number | null | undefined, digits = 2): string {
  return n === null || n === undefined || Number.isNaN(n) ? '—' : n.toFixed(digits);
}

export function pct(n: number): string {
  return `${Math.round(n)}%`;
}

// Engagement status → chip label + classes.
export function statusMeta(status: string): { label: string; chipClass: string } {
  const map: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700 ring-gray-200',
    ready: 'bg-sky-50 text-sky-700 ring-sky-200',
    active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    closed: 'bg-gray-200 text-gray-600 ring-gray-300',
    imported: 'bg-amber-50 text-amber-800 ring-amber-200',
  };
  return {
    label: status.charAt(0).toUpperCase() + status.slice(1),
    chipClass: map[status] ?? 'bg-gray-100 text-gray-700 ring-gray-200',
  };
}

export function sourceIsImported(source: string): boolean {
  return source.startsWith('imported');
}

// Source → short tag label. Imported sources spell out the granularity.
export function sourceLabel(source: string): string {
  switch (source) {
    case 'native':
      return 'Native';
    case 'imported_dimension_level':
      return 'Imported · dimension-level';
    case 'imported_question_level':
      return 'Imported · question-level';
    case 'imported_raw_response':
      return 'Imported · raw-response';
    default:
      return sourceIsImported(source) ? 'Imported' : source;
  }
}

// The five maturity tiers (SPEC), used to label distribution segments.
export const LEVEL_LABELS: [string, string, string, string, string] = [
  '1 · Initial',
  '2 · Repeatable',
  '3 · Defined',
  '4 · Managed',
  '5 · Optimized',
];

// The maturity colour ramp now lives in the shared color util so the respondent
// heatmap and these distribution bars draw from one implementation. Re-exported
// here so existing admin imports (AnalysisDashboard) are unchanged.
export { maturityRamp } from '../color.ts';
